import { useStoreState } from 'easy-peasy'

import {
  ffKey,
  FEATURE_FLAG_TYPES,
  LOCAL_FF_OVERRIDES,
  hasLocalOverrideFlag,
  DEFAULT_FEATURE_FLAGS,
} from '@mortgage-pos/data'

// Imported from easy-peasy directly to avoid a circular import (store modules
// already import `variation` from this file).

let testFlagValues = DEFAULT_FEATURE_FLAGS

function waitForFeatureFlagsLoaded(store: {
  getState: () => { featureFlags: { loaded: boolean } }
  subscribe: (listener: () => void) => () => void
}): Promise<void> {
  if (store.getState().featureFlags.loaded) return Promise.resolve()

  return new Promise((resolve) => {
    const unsubscribe = store.subscribe(() => {
      if (store.getState().featureFlags.loaded) {
        unsubscribe()
        resolve()
      }
    })
  })
}

export function useVariation(flag: string, defaultValue?: any) {
  // Short-circuit before hooks so stories/tests without a StoreProvider
  // don't crash inside useStoreState. Branch is env-stable per render.
  if (process.env.NODE_ENV === 'test' || process.env.STORYBOOK === 'true') {
    return testFlagValues[flag]
  }

  if (hasLocalOverrideFlag(flag)) {
    return LOCAL_FF_OVERRIDES[flag]
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  const customValues = useStoreState(
    (state: { featureFlags: { values: Record<string, any> } }) =>
      state.featureFlags.values
  )

  const customValue = customValues[flag]
  if (customValue !== undefined) return customValue
  if (defaultValue !== undefined) return defaultValue
  return DEFAULT_FEATURE_FLAGS[flag]
}

export async function variation(flag: string): Promise<unknown> {
  if (process.env.NODE_ENV === 'test') {
    return testFlagValues[flag]
  }

  if (hasLocalOverrideFlag(flag)) {
    return LOCAL_FF_OVERRIDES[flag]
  }

  // Lazy require avoids a circular import (store thunks import this file).
  const store = require('@mortgage-pos/ui/store').default

  // Block until the initial flag fetch completes so callers see real values
  // rather than defaults.
  await waitForFeatureFlagsLoaded(store)

  const value = store.getState().featureFlags.values[flag]
  return value !== undefined ? value : DEFAULT_FEATURE_FLAGS[flag]
}

export async function variationByKey(flagKey: ffKey) {
  const flag = FEATURE_FLAG_TYPES[flagKey]
  if (hasLocalOverrideFlag(flag)) return LOCAL_FF_OVERRIDES[flag]
  return variation(flag)
}

/** No-op: flags are loaded by the Easy Peasy store on app start. */
export async function initializeFeatureFlags(): Promise<void> {}

/** Testing utilities */

export function setFeatureFlags(testFlags) {
  testFlagValues = { ...DEFAULT_FEATURE_FLAGS, ...testFlags }
}

export function resetFeatureFlags() {
  testFlagValues = DEFAULT_FEATURE_FLAGS
}
